import { Component, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ImageUrlPipe, InsightMarkdownPipe } from '@frontend/shared';
import { TranslocoModule, provideTranslocoScope } from '@jsverse/transloco';
import { finalize, map } from 'rxjs/operators';
import { reloadOnLocaleChange } from '../../language';
import { LoaderGlobeAnimationDetailComponent } from '../../loaders';
import { DEFAULT_SHOWS_DEFINITION, ShowsDefinition, Tool, ToolType } from '../shows.model';
import { ShowsService } from '../shows.service';
import { toolTypeKey } from '../tool-type';

const TOOL_TYPES: ToolType[] = ['exercise', 'book', 'website', 'video', 'other'];

/**
 * Every tool, by name: `/tools`, with `?type=` to show one kind. Tools are the platform's, not a
 * show's.
 */
@Component({
  selector: 'multisite-tools-index',
  imports: [RouterModule, TranslocoModule, ImageUrlPipe, InsightMarkdownPipe, LoaderGlobeAnimationDetailComponent],
  providers: [provideTranslocoScope('shows', 'tools')],
  template: `
    <div class="container my-4" *transloco="let t">
      <h1 class="font-weight-bold mb-3">{{ t('tools.tools') }}</h1>

      <div class="btn-group flex-wrap mb-4" role="group">
        <button type="button" class="btn btn-sm" [class.btn-primary]="type() === null" [class.btn-outline-primary]="type() !== null" (click)="chooseType(null)">{{ t('common.all') }}</button>
        @for (option of types; track option) {
          <button type="button" class="btn btn-sm" [class.btn-primary]="type() === option" [class.btn-outline-primary]="type() !== option" (click)="chooseType(option)">
            {{ t(toolTypeKey(option)) }}
          </button>
        }
      </div>

      @if (loading() && !tools().length) {
        <multisite-loader-globe-animation-detail [width]="100" [height]="100"></multisite-loader-globe-animation-detail>
      }

      @if (failed()) {
        <p class="text-danger">{{ t('error.could_not_load') }}</p>
      }

      <div class="row">
        @for (tool of tools(); track tool.id) {
          <div class="col-md-6 col-lg-4 mb-4">
            <a class="card h-100 text-reset text-decoration-none" [routerLink]="[definition.toolsPath, tool.slug]">
              @if (tool.images[0]; as image) {
                <img class="card-img-top" [src]="image | imageUrl: 'w_400,c_fill,ar_16:9'" [alt]="image.alt_text ?? ''" />
              }
              <div class="card-body">
                <small class="text-muted d-block">{{ t(toolTypeKey(tool.type)) }}</small>
                <p class="h5 card-title font-weight-bold">{{ tool.name }}</p>
                @if (tool.summary) {
                  <div class="card-text small text-muted" [innerHTML]="tool.summary | insightMarkdown"></div>
                }
              </div>
            </a>
          </div>
        } @empty {
          @if (!loading() && !failed()) {
            <p>{{ t('common.no_results') }}</p>
          }
        }
      </div>

      <a class="btn btn-link px-0" [routerLink]="[definition.showsPath]">{{ t('shows.all_shows') }} <i class="fa-solid fa-angle-right ml-1"></i></a>
    </div>
  `,
})
export class ToolsIndexComponent {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private showsService = inject(ShowsService);

  readonly definition: ShowsDefinition = this.route.snapshot.data['showsSection'] ?? DEFAULT_SHOWS_DEFINITION;
  readonly types = TOOL_TYPES;
  readonly toolTypeKey = toolTypeKey;
  readonly type = toSignal(this.route.queryParamMap.pipe(map((params) => ToolsIndexComponent.readType(params.get('type')))), { initialValue: null });

  tools = signal<Tool[]>([]);
  loading = signal(false);
  failed = signal(false);

  constructor() {
    this.route.queryParamMap.subscribe(() => this.load());
    reloadOnLocaleChange(() => this.load());
  }

  static readType(value: string | null): ToolType | null {
    return TOOL_TYPES.includes(value as ToolType) ? (value as ToolType) : null;
  }

  chooseType(type: ToolType | null): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { type } });
  }

  private load(): void {
    const type = ToolsIndexComponent.readType(this.route.snapshot.queryParamMap.get('type'));

    this.loading.set(true);
    this.failed.set(false);
    this.showsService
      .getTools({ filter: type ? { type: [type] } : {}, pageSize: 100, include: ['images'] })
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({ next: (page) => this.tools.set(page.items), error: () => this.failed.set(true) });
  }
}
